/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
type Point = { x: number; y: number }; // 表示一个点的坐标
type PointKey = number; // 表示点的唯一标识符

const RELAXATION_DISTANCE = 1; // 邻居的放松距离
const MIN_SCC_SIZE = 3; // 最小强连通分量大小

// 将点转换为唯一标识符
const pointToId = (point: Point, maxCols: number): PointKey => (point.y * maxCols) + point.x;

// 将唯一标识符转换为点
const idToPoint = (id: PointKey, maxCols: number): Point => ({
    x: id % maxCols,
    y: Math.floor(id / maxCols),
});

// 获取点的放松邻居
const getRelaxedNeighbors = (pointId: PointKey, maxCols: number, maxRows: number, pointSet: Uint8Array): PointKey[] => {
    const neighbors: PointKey[] = [];
    const { x, y } = idToPoint(pointId, maxCols);
    // 遍历放松距离内的所有点
    for (let dy = -RELAXATION_DISTANCE; dy <= RELAXATION_DISTANCE; dy++) {
        for (let dx = -RELAXATION_DISTANCE; dx <= RELAXATION_DISTANCE; dx++) {
            if (dx !== 0 || dy !== 0) {
                // 排除自身
                const nx = x + dx;
                const ny = y + dy;
                // 确保新点在有效范围内且存在于点集内
                if (nx >= 0 && nx < maxCols && ny >= 0 && ny < maxRows && pointSet[(ny * maxCols) + nx] === 1) {
                    neighbors.push((ny * maxCols) + nx);
                }
            }
        }
    }
    return neighbors;
};

// 获取强连通分量
const getScc = (scc: PointKey[]): PointKey[] => {
    const uniquePoints = new Set<PointKey>();
    const result: PointKey[] = [];

    for (const pointId of scc) {
        if (!uniquePoints.has(pointId)) {
            result.push(pointId);
            uniquePoints.add(pointId);
        }
    }

    return result;
};
// 获取强连通分量的标识符
const getSccIdentifier = (scc: PointKey[]): string => {
    scc.sort((a, b) => a - b);
    return scc.join(',');
};

const tarjanIterative = (
    startPointId: PointKey,
    neighbors: PointKey[][],
    index: Uint32Array,
    lowLink: Uint32Array,
    stack: PointKey[],
    onStack: Uint8Array,
    currentIndex: { value: number },
    result: Set<string>,
) => {
    const stackToProcess: [number, 0 | 1][] = [[startPointId, 0]];

    while (stackToProcess.length) {
        const [pointId, phase] = stackToProcess.pop() as [number, 0 | 1];

        if (phase === 0) {
            // eslint-disable-next-line no-plusplus, no-multi-assign
            index[pointId] = lowLink[pointId] = currentIndex.value++;
            stack.push(pointId);
            onStack[pointId] = 1;

            stackToProcess.push([pointId, 1]);
            for (const neighbor of neighbors[pointId]) {
                if (!index[neighbor]) {
                    stackToProcess.push([neighbor, 0]);
                } else if (onStack[neighbor]) {
                    lowLink[pointId] = Math.min(lowLink[pointId], index[neighbor]);
                }
            }
        } else {
            neighbors[pointId].forEach((neighbor) => {
                if (onStack[neighbor]) {
                    lowLink[pointId] = Math.min(lowLink[pointId], lowLink[neighbor]);
                }
            });

            if (lowLink[pointId] === index[pointId]) {
                const component: PointKey[] = [];
                let node: PointKey;
                do {
                    node = stack.pop() as PointKey;
                    onStack[node] = 0;
                    component.push(node);
                } while (node !== pointId);

                const scc = getScc(component);
                if (scc.length >= MIN_SCC_SIZE) result.add(getSccIdentifier(scc));
            }
        }
    }
};

// 寻找所有强连通分量
export const findSccs = (points: Point[]): Point[][] => {
    if (points.length === 0) return [];

    const maxCols = Math.max(...points.map(p => p.x)) + 1;
    const maxRows = Math.max(...points.map(p => p.y)) + 1;
    const totalSize = maxCols * maxRows;

    const pointSet = new Uint8Array(totalSize);

    // 填充点集
    points.forEach(p => (pointSet[pointToId(p, maxCols)] = 1));

    // 获取所有点的邻居
    const neighbors = Array.from({ length: totalSize }, (_, i) =>
        (pointSet[i] ? getRelaxedNeighbors(i, maxCols, maxRows, pointSet) : []));

    const index = new Uint32Array(totalSize);
    const lowLink = new Uint32Array(totalSize);
    const stack: PointKey[] = [];
    const onStack = new Uint8Array(totalSize);
    const currentIndex = { value: 1 };
    const result = new Set<string>();

    // 对每个点执行Tarjan算法
    for (let pointId = 0; pointId < totalSize; pointId++) {
        if (pointSet[pointId] && !index[pointId]) {
            tarjanIterative(pointId, neighbors, index, lowLink, stack, onStack, currentIndex, result);
        }
    }

    // 将结果转换为点数组形式
    return Array.from(result).map(sccIdentifier =>
        sccIdentifier.split(',').map(id => idToPoint(parseInt(id, 10), maxCols)));
};
